import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Award,
  Users,
  MessageSquare,
  Lightbulb,
  TrendingUp,
  Calendar,
  ArrowRight,
} from 'lucide-react';

interface WelcomeOverviewProps {
  championName: string;
  championNumber: number;
  totalChampions: number;
  daysActive: number;
  neighborsReached: number;
  feedbackCollected: number;
  featureIdeasSubmitted: number;
  currentPhase: 'testing' | 'feedback' | 'launch-prep' | 'launched';
}

const phaseLabels = {
  testing: 'Testing Phase',
  feedback: 'Feedback Collection',
  'launch-prep': 'Launch Preparation',
  launched: 'Platform Launched',
};

const phaseColors = {
  testing: 'bg-blue-500',
  feedback: 'bg-purple-500',
  'launch-prep': 'bg-orange-500',
  launched: 'bg-green-500',
};

export function WelcomeOverview({
  championName,
  championNumber,
  totalChampions,
  daysActive,
  neighborsReached,
  feedbackCollected,
  featureIdeasSubmitted,
  currentPhase,
}: WelcomeOverviewProps) {
  return (
    <div className="space-y-6">
      {/* Welcome Card */}
      <Card className="bg-gradient-to-br from-primary/10 via-primary/5 to-background border-primary/20">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="space-y-1">
              <CardTitle className="text-3xl">👋 Welcome back, {championName}!</CardTitle>
              <CardDescription className="text-base">
                You're Champion #{championNumber} of {totalChampions} for Lake Charles
              </CardDescription>
            </div>
            <Badge variant="outline" className="shrink-0">
              <div className={`w-2 h-2 rounded-full ${phaseColors[currentPhase]} mr-2`} />
              {phaseLabels[currentPhase]}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Calendar className="h-4 w-4" />
            <span>Active for {daysActive} days</span>
          </div>
        </CardContent>
      </Card>

      {/* Impact Metrics */}
      <div>
        <h2 className="text-xl font-semibold mb-4">Your Impact This Week</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Neighbors Reached */}
          <Card className="hover:shadow-lg transition-shadow">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-2xl font-bold text-primary">{neighborsReached}</p>
                  <p className="text-sm text-muted-foreground mt-1">Neighbors Reached</p>
                </div>
                <div className="p-3 bg-primary/10 rounded-lg">
                  <Users className="h-6 w-6 text-primary" />
                </div>
              </div>
              <div className="mt-4 flex items-center gap-1 text-xs text-green-600">
                <TrendingUp className="h-3 w-3" />
                <span>+4 from last week</span>
              </div>
            </CardContent>
          </Card>

          {/* Feedback Collected */}
          <Card className="hover:shadow-lg transition-shadow">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-2xl font-bold text-primary">{feedbackCollected}</p>
                  <p className="text-sm text-muted-foreground mt-1">Feedback Collected</p>
                </div>
                <div className="p-3 bg-primary/10 rounded-lg">
                  <MessageSquare className="h-6 w-6 text-primary" />
                </div>
              </div>
              <div className="mt-4 flex items-center gap-1 text-xs text-green-600">
                <TrendingUp className="h-3 w-3" />
                <span>+3 from last week</span>
              </div>
            </CardContent>
          </Card>

          {/* Feature Ideas */}
          <Card className="hover:shadow-lg transition-shadow">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-2xl font-bold text-primary">{featureIdeasSubmitted}</p>
                  <p className="text-sm text-muted-foreground mt-1">Feature Ideas Submitted</p>
                </div>
                <div className="p-3 bg-primary/10 rounded-lg">
                  <Lightbulb className="h-6 w-6 text-primary" />
                </div>
              </div>
              <div className="mt-4 flex items-center gap-1 text-xs text-muted-foreground">
                <span>Keep the ideas coming!</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Quick Actions</CardTitle>
          <CardDescription>Jump into your Champion tasks</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Button variant="outline" className="justify-start h-auto py-4 group" asChild>
              <a href="/champions/dashboard/feedback">
                <MessageSquare className="h-5 w-5 mr-3 text-primary group-hover:text-white transition-colors" />
                <div className="text-left">
                  <p className="font-medium">Collect Feedback</p>
                  <p className="text-xs text-muted-foreground group-hover:text-white transition-colors">
                    Gather neighbor input
                  </p>
                </div>
                <ArrowRight className="h-4 w-4 ml-auto group-hover:text-white transition-colors" />
              </a>
            </Button>

            <Button variant="outline" className="justify-start h-auto py-4 group" asChild>
              <a href="/champions/dashboard/demos">
                <Award className="h-5 w-5 mr-3 text-primary group-hover:text-white transition-colors" />
                <div className="text-left">
                  <p className="font-medium">Share Feature Demos</p>
                  <p className="text-xs text-muted-foreground group-hover:text-white transition-colors">
                    Show neighbors the platform
                  </p>
                </div>
                <ArrowRight className="h-4 w-4 ml-auto group-hover:text-white transition-colors" />
              </a>
            </Button>

            <Button variant="outline" className="justify-start h-auto py-4 group" asChild>
              <a href="/champions/dashboard/voting">
                <TrendingUp className="h-5 w-5 mr-3 text-primary group-hover:text-white transition-colors" />
                <div className="text-left">
                  <p className="font-medium">Vote on Priorities</p>
                  <p className="text-xs text-muted-foreground group-hover:text-white transition-colors">
                    Help us prioritize
                  </p>
                </div>
                <ArrowRight className="h-4 w-4 ml-auto group-hover:text-white transition-colors" />
              </a>
            </Button>

            <Button variant="outline" className="justify-start h-auto py-4 group" asChild>
              <a href="/champions/dashboard/directory">
                <Users className="h-5 w-5 mr-3 text-primary group-hover:text-white transition-colors" />
                <div className="text-left">
                  <p className="font-medium">Champions Directory</p>
                  <p className="text-xs text-muted-foreground group-hover:text-white transition-colors">
                    Connect with fellow Champions
                  </p>
                </div>
                <ArrowRight className="h-4 w-4 ml-auto group-hover:text-white transition-colors" />
              </a>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Getting Started Tips */}
      <Card className="bg-muted/50">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Lightbulb className="h-5 w-5 text-primary" />
            Champion Tips
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-3 text-sm">
            <li className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                <span className="text-xs font-bold text-primary">1</span>
              </div>
              <div>
                <p className="font-medium">Start with your closest neighbors</p>
                <p className="text-muted-foreground">
                  Talk to 3-5 neighbors you know well. Ask what community issues matter most to
                  them.
                </p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                <span className="text-xs font-bold text-primary">2</span>
              </div>
              <div>
                <p className="font-medium">Share demo videos</p>
                <p className="text-muted-foreground">
                  Use the Feature Demos section to get shareable links. Post them in your Facebook
                  groups.
                </p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                <span className="text-xs font-bold text-primary">3</span>
              </div>
              <div>
                <p className="font-medium">Log all feedback</p>
                <p className="text-muted-foreground">
                  Even negative feedback is valuable. Use the Collect Feedback form to track
                  everything.
                </p>
              </div>
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
